import { useCallback, useState } from "react";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { IconButton, InputAdornment, TextField } from "@mui/material";

import { PasswordResetUser } from "@/types/passwordReset";
import { EntityProps } from "@/types/util";
import { ValidationParams } from "@/types/validation";

import { handleChange } from "@/lib/validation";
import { passwordValidation } from "@/lib/validation/password";

import PasswordRules from "@/components/passwordReset/rules";

export default function PasswordForm<
  T extends Pick<PasswordResetUser, "password" | "passwordConfirmation">
>(props: EntityProps<T>) {
  const [showPasswordValue, setShowPasswordValue] = useState<boolean>(false);
  const [showPasswordConfirmationValue, setShowPasswordConfirmationValue] =
    useState<boolean>(false);

  const onChangeParams = useCallback<
    () => Pick<
      ValidationParams<
        T,
        Pick<PasswordResetUser, "password" | "passwordConfirmation">
      >,
      "validation" | "setEntity" | "setErrors"
    >
  >(() => {
    return {
      validation: passwordValidation,
      setEntity: props.set,
      setErrors: props.setErrors,
    };
  }, [props.set, props.setErrors]);

  function onChange(
    value: string,
    field: keyof Pick<PasswordResetUser, "password" | "passwordConfirmation">,
    confirmationValue?: string
  ) {
    const { validation, setEntity, setErrors } = onChangeParams();

    handleChange<
      T,
      Pick<PasswordResetUser, "password" | "passwordConfirmation">
    >({ confirmationValue, field, setEntity, setErrors, validation, value });
  }

  return (
    <>
      <PasswordRules />
      <TextField
        key="password-value"
        error={props.errors.password !== undefined}
        helperText={props.errors.password}
        label="Password"
        fullWidth
        onChange={(event) => {
          onChange(event.target.value, "password");
          onChange(
            props?.entity?.passwordConfirmation ?? "",
            "passwordConfirmation",
            event.target.value
          );
        }}
        variant="standard"
        type={showPasswordValue ? "text" : "password"}
        slotProps={{
          input: {
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowPasswordValue((prev) => !prev)}
                  edge="end"
                >
                  {showPasswordValue ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          },
        }}
        value={props.entity?.password ?? ""}
      />
      <TextField
        key="confirm-password-value"
        error={props.errors.passwordConfirmation !== undefined}
        helperText={props.errors.passwordConfirmation}
        label="Confirm Password"
        fullWidth
        onChange={(event) =>
          onChange(
            event.target.value,
            "passwordConfirmation",
            props.entity?.password ?? ""
          )
        }
        variant="standard"
        type={showPasswordConfirmationValue ? "text" : "password"}
        slotProps={{
          input: {
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() =>
                    setShowPasswordConfirmationValue((prev) => !prev)
                  }
                  edge="end"
                >
                  {showPasswordConfirmationValue ? (
                    <VisibilityOff />
                  ) : (
                    <Visibility />
                  )}
                </IconButton>
              </InputAdornment>
            ),
          },
        }}
        value={props.entity?.passwordConfirmation ?? ""}
      />
    </>
  );
}
